import { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Button,
  Paper,
  Grid,
  Collapse,
  IconButton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";

const StatisticsFilterPanel = ({ filterOptions, filters, onFiltersChange }) => {
  const { t } = useTranslation("homepage");
  const [expanded, setExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      sex: "",
      age_min: filterOptions?.age_range?.min || 0,
      age_max: filterOptions?.age_range?.max || 100,
      country: "",
      education: "",
      region: "",
      risk_level: "",
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = () => {
    return (
      localFilters.sex ||
      localFilters.country ||
      localFilters.education ||
      localFilters.region ||
      localFilters.risk_level ||
      (filterOptions?.age_range &&
        (localFilters.age_min !== filterOptions.age_range.min ||
          localFilters.age_max !== filterOptions.age_range.max))
    );
  };

  if (!filterOptions || Object.keys(filterOptions).length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 4,
        mx: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          cursor: "pointer",
          bgcolor: expanded ? "primary.light" : "transparent",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" fontWeight={500}>
            {t("filters.title")}
          </Typography>
          {hasActiveFilters() && (
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "white",
                borderRadius: "50%",
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
              }}
            >
              !
            </Box>
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 3, pt: 1 }}>
          <Grid container spacing={3}>
            {/* Sex Filter */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("filters.sex")}</InputLabel>
                <Select
                  value={localFilters.sex || ""}
                  label={t("filters.sex")}
                  onChange={(e) => handleFilterChange("sex", e.target.value)}
                >
                  <MenuItem value="">{t("filters.all")}</MenuItem>
                  {filterOptions.sex?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`gender.${option.toLowerCase()}`, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Age Range Filter */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Typography gutterBottom variant="body2">
                {t("filters.ageRange")}: {localFilters.age_min} - {localFilters.age_max}
              </Typography>
              <Slider
                value={[localFilters.age_min || 0, localFilters.age_max || 100]}
                onChange={(e, newValue) => {
                  handleFilterChange("age_min", newValue[0]);
                  handleFilterChange("age_max", newValue[1]);
                }}
                valueLabelDisplay="auto"
                min={filterOptions.age_range?.min || 0}
                max={filterOptions.age_range?.max || 100}
                sx={{ mt: 1 }}
              />
            </Grid>

            {/* Country Filter */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("filters.country")}</InputLabel>
                <Select
                  value={localFilters.country || ""}
                  label={t("filters.country")}
                  onChange={(e) => handleFilterChange("country", e.target.value)}
                >
                  <MenuItem value="">{t("filters.all")}</MenuItem>
                  {filterOptions.countries?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`countries.${option}`, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Education Filter */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("filters.education")}</InputLabel>
                <Select
                  value={localFilters.education || ""}
                  label={t("filters.education")}
                  onChange={(e) => handleFilterChange("education", e.target.value)}
                >
                  <MenuItem value="">{t("filters.all")}</MenuItem>
                  {filterOptions.education?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`education.${option}`, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Region Filter */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("filters.region")}</InputLabel>
                <Select
                  value={localFilters.region || ""}
                  label={t("filters.region")}
                  onChange={(e) => handleFilterChange("region", e.target.value)}
                >
                  <MenuItem value="">{t("filters.all")}</MenuItem>
                  {filterOptions.regions?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`environment.${option}`, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Risk Level Filter */}
            <Grid item xs={12} sm={6} md={4} lg={1}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("filters.riskLevel")}</InputLabel>
                <Select
                  value={localFilters.risk_level ?? ""}
                  label={t("filters.riskLevel")}
                  onChange={(e) =>
                    handleFilterChange(
                      "risk_level",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                >
                  <MenuItem value="">{t("filters.all")}</MenuItem>
                  {filterOptions.risk_levels?.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!hasActiveFilters()}
            >
              {t("filters.clear")}
            </Button>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              onClick={handleApplyFilters}
            >
              {t("filters.apply")}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default StatisticsFilterPanel;
